/**
 * POS Action Bar
 * Top-positioned action bar with Home, Split Check, History, Park Sale, and Clear Cart buttons
 * Always visible during the ordering process - moved to TOP to not block menu items
 * Optimized for tablet touch targets (min 44px height)
 */
import { Home, PauseCircle, Trash2, ShoppingCart, Users, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function POSActionBar({
  cartCount,
  parkedSalesCount,
  onGoHome,
  onParkSale,
  onClearCart,
  onShowParkedSales,
  onSplitCheck,
  onOrderHistory,
  disabled = false
}) {
  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 lg:py-3 flex items-center justify-center gap-2 lg:gap-3 shadow-sm">
      {/* Home Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onGoHome}
        disabled={disabled}
        className="rounded-lg h-11 w-11 lg:h-12 lg:w-12 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600"
        title="Go to Home / Customer Lookup"
        data-testid="pos-home-btn"
      >
        <Home className="w-5 h-5 lg:w-6 lg:h-6" />
      </Button>
      
      {/* Split Check / Table Bill Button - PROMINENT */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onSplitCheck}
        disabled={disabled}
        className="rounded-lg h-11 lg:h-12 px-4 lg:px-5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 font-medium text-sm lg:text-base"
        title="Split Check / Pay Table Bill"
        data-testid="pos-split-check-btn"
      >
        <Users className="w-4 h-4 lg:w-5 lg:h-5 mr-1.5" />
        Split Check
      </Button>
      
      {/* Order History Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onOrderHistory}
        disabled={disabled}
        className="rounded-lg h-11 lg:h-12 px-4 lg:px-5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-sm lg:text-base"
        title="Order History (2 days) - Look up phone numbers, reprint receipts"
        data-testid="pos-order-history-btn"
      >
        <Clock className="w-4 h-4 lg:w-5 lg:h-5 mr-1.5" />
        History
      </Button>
      
      {/* Divider */}
      <div className="w-px h-8 lg:h-10 bg-gray-200 dark:bg-gray-700" />
      
      {/* Park Sale Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onParkSale}
        disabled={disabled || cartCount === 0}
        className="rounded-lg h-11 lg:h-12 px-4 lg:px-5 hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-600 disabled:opacity-50 text-sm lg:text-base"
        title="Park this sale for later"
        data-testid="pos-park-sale-btn"
      >
        <PauseCircle className="w-4 h-4 lg:w-5 lg:h-5 mr-1.5" />
        Park
      </Button>
      
      {/* Clear Cart Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          if (cartCount > 0 && window.confirm('Clear all items from cart?')) {
            onClearCart();
          }
        }}
        disabled={disabled || cartCount === 0}
        className="rounded-lg h-11 lg:h-12 px-4 lg:px-5 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 disabled:opacity-50 text-sm lg:text-base"
        title="Clear cart and start over"
        data-testid="pos-clear-cart-btn"
      >
        <Trash2 className="w-4 h-4 lg:w-5 lg:h-5 mr-1.5" />
        Clear
      </Button>
      
      {/* Divider */}
      <div className="w-px h-8 lg:h-10 bg-gray-200 dark:bg-gray-700" />
      
      {/* Parked Sales Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onShowParkedSales}
        disabled={disabled}
        className={`rounded-lg h-11 lg:h-12 px-4 lg:px-5 relative text-sm lg:text-base ${
          parkedSalesCount > 0 
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 hover:bg-amber-200 dark:hover:bg-amber-900/50' 
            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title="View parked sales"
        data-testid="pos-parked-sales-btn"
      >
        <ShoppingCart className="w-4 h-4 lg:w-5 lg:h-5 mr-1.5" />
        Parked
        {parkedSalesCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 lg:w-6 lg:h-6 bg-amber-500 text-white text-xs lg:text-sm rounded-full flex items-center justify-center font-bold animate-pulse">
            {parkedSalesCount}
          </span>
        )}
      </Button>
    </div>
  );
}
